<script setup lang="ts">
    import { ref, computed, watch, onMounted, toRef } from 'vue'
    import { formatStringForDateInput } from "@common/Common"

    import type { AlternativeEditorAttributeFieldReadModel } from "@Alternatives/Alternatives/Domain/readmodels"
    import type { ValidationError } from "@common/types"

    /* props */

    const props = defineProps<{
        propF: AlternativeEditorAttributeFieldReadModel
        propErrors: ValidationError[]
    }>()

    /* aliases */

    const f = toRef(props, 'propF')
    const errors = toRef(props, 'propErrors')

    /* derived */

    const error = computed(() =>
        errors.value.find(e =>
            e.Context.startsWith(`AlternativeAttribute:${f.value.Id}`)
        )?.Error
    )

    const definedValues = computed(() =>
        f.value.DefinedValues.map(dv => ({
            Value: formatStringForDateInput(dv.Value),
            Label: dv.Label
        }))
    )

    /* local state */

    const value = ref<string | null>(null)

    /* sync back to domain */

    watch(value, newValue =>
    {
        f.value.Value = newValue
    })

    /* init */

    onMounted(() =>
    {
        value.value = formatStringForDateInput(f.value.Value)
    })
</script>

<template>
    <div class="form-group">
        <label>
            <span v-if="f.IsRequired" style="color: red;">*</span> {{ f.Name }}
        </label>

        <select v-model="value"
                class="form-control"
                :required="f.IsRequired"
                :disabled="f.IsReadonly">
            <option :value="null">Choose...</option>
            <option v-for="dv in definedValues"
                    :key="dv.Value"
                    :value="dv.Value">
                {{ dv.Label }}
            </option>
        </select>

        <div class="error">{{ error }}</div>

        <small v-if="f.Description" class="form-text text-muted">
            {{ f.Description }}
        </small>
    </div>
</template>
